import { useEffect, useRef, useState } from "react";
import { strategyDocument } from "@strat/format";
import type { ExportRequest, RunSettings, StrategySpec } from "@strat/format";

import { downloadDocument } from "./file";
import { useExport } from "./useExport";

/**
 * "↓ Export ▾" for a backtest — or a set of them: the full result as a
 * `.backtest`, the same gzipped as a `.backtest.gz`, or only its rules as a
 * `.strategy`. One menu everywhere a result is shown, in the browser and the
 * desktop app alike, so the three choices read the same wherever they appear.
 *
 * `request` may be a function, for exports whose ids have to be collected
 * first ("everything matching these filters"); returning null cancels.
 */
export function ExportMenu({
  request,
  strategy,
  label = "Export",
  size = "sm",
  onExported,
}: {
  request: ExportRequest | (() => Promise<ExportRequest | null>);
  /** The rules on their own, when "strategy only" makes sense (one run). */
  strategy?: { spec: StrategySpec; settings?: RunSettings };
  label?: string;
  size?: "sm" | "md";
  onExported?: (message: string) => void;
}) {
  const { exportItems, busy, error } = useExport();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  async function backtest(compressed: boolean) {
    setOpen(false);
    const req = typeof request === "function" ? await request() : request;
    if (!req) return;
    if (await exportItems(req, { compressed })) {
      onExported?.(compressed ? "Saved a compressed .backtest.gz." : "Saved a .backtest file.");
    }
  }

  async function rulesOnly() {
    setOpen(false);
    if (!strategy) return;
    const name = await downloadDocument(
      strategyDocument(
        strategy.spec,
        new Date().toISOString(),
        strategy.settings ? { settings: strategy.settings } : {},
      ),
    );
    onExported?.(`Saved ${name}.`);
  }

  return (
    <div className="export-menu" ref={ref}>
      <button
        type="button"
        className={`btn${size === "sm" ? " btn--sm" : ""}`}
        aria-haspopup="menu"
        aria-label={label ? undefined : "Export"}
        title={label ? undefined : "Export as .backtest, .backtest.gz or .strategy"}
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        {busy ? "Exporting…" : `↓${label ? ` ${label}` : ""}`} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="export-menu__list" role="menu">
          <button type="button" role="menuitem" onClick={() => void backtest(false)}>
            <strong>Backtest</strong> <code>.backtest</code>
            <span className="muted small">Rules, settings and the full result, as JSON.</span>
          </button>
          <button type="button" role="menuitem" onClick={() => void backtest(true)}>
            <strong>Backtest, compressed</strong> <code>.backtest.gz</code>
            <span className="muted small">The same file, about a tenth of the size.</span>
          </button>
          {strategy && (
            <button type="button" role="menuitem" onClick={() => void rulesOnly()}>
              <strong>Strategy only</strong> <code>.strategy</code>
              <span className="muted small">The rules and where they were tested, no result.</span>
            </button>
          )}
          <p className="export-menu__note muted small">
            Market data is not included: the file names its provider, symbol and window, and opening it
            fetches the same bars.
          </p>
        </div>
      )}
      {error && (
        <span className="export-menu__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
